import {
  Component,
  ComponentRef,
  computed,
  inject,
  inputBinding,
  resource,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { ApiClient } from '@core/services/api-client';
import { Badge } from '@shared/components/badge/badge';
import { Button } from '@shared/components/button/button';
import { Modal } from '@shared/components/modal/modal';
import { Toast, ToastMessage } from '@shared/components/toast/toast';
import { TestSet } from '@shared/models/test-sets';

@Component({
  selector: 'app-test-sets-page',
  imports: [Button, Badge, Modal],
  templateUrl: './test-sets-page.html',
})
export class TestSetsPage {
  private apiClient = inject(ApiClient);
  private router = inject(Router);

  // Toast functionality
  private toastRef: ComponentRef<Toast> | null = null;
  readonly toastContainer = viewChild.required('toastOutlet', {
    read: ViewContainerRef,
  });
  private toastMessage = signal<string>('');

  // Delete modal
  readonly deleteModal = viewChild.required('deleteModal', { read: Modal });
  private testSetToDelete = signal<string>('');

  // Test set Data
  readonly testSets = resource({
    loader: () => this.apiClient.getTestSets(),
  });

  readonly isTestSetsLoading = computed(
    () => this.testSets.status() === 'loading'
  );

  readonly testSetsData = computed(
    () => this.testSets.value() || ([] as TestSet[])
  );

  // Toast functionality
  private showToast(type: ToastMessage['type'], message: string): void {
    if (!this.toastRef) {
      this.toastRef = this.toastContainer().createComponent(Toast, {
        bindings: [inputBinding('toastMessage', this.toastMessage)],
      });
    }
    this.toastMessage.set(message);
    this.toastRef.instance.show(type, message);
  }

  async onDeleteTestSet(testSetId: string) {
    this.testSetToDelete.set(testSetId);
    this.deleteModal().open();
  }

  async confirmDelete() {
    try {
      const testSetId = this.testSetToDelete();
      if (!testSetId) return;

      await this.apiClient.deleteTestSets(testSetId);
      this.testSets.reload();
      this.showToast('success', 'Test set deleted successfully');
      this.testSetToDelete.set('');
    } catch (error) {
      console.error('Failed to delete test set:', error);
      this.showToast('error', 'Failed to delete test set. Please try again.');
    }
  }

  goToEditTestSet(testSetId: string) {
    // Navigate to the edit page for the selected test set
    this.router.navigate(['/test-sets/edit', testSetId]);
  }

  goToViewTestSet(testSetId: string) {
    // Navigate to the view page for the selected test set
    this.router.navigate(['/test-sets/view', testSetId]);
  }

  goToCreateTestSet() {
    // Navigate to the create page for a new test set
    this.router.navigate(['/test-sets/create']);
  }
}
